/**
 * Dates written the way the person reading them writes dates.
 *
 * The locale comes from the browser rather than from the translation language,
 * because the two answer different questions and disagree often. Someone in Berlin
 * who never translates still wants `21.10.2019`, and their translation language is
 * "none". Someone in Chicago following a Spanish link still wants `10/21/2019`,
 * because their date conventions did not change when the prose did.
 *
 * `locale` is a parameter only so tests can pin one -- production callers leave it
 * undefined and get the browser's own. It is never threaded through components.
 */

/** Any date works; this one just has a two-digit day and month to read in a diff. */
const REFERENCE_DATE = new Date(2019, 9, 21);

/**
 * Two-digit tokens rather than `d`/`M`. They give a fixed-width field to type into,
 * and they reproduce the previous fixed `MM/dd/yyyy` exactly for a US locale, so
 * nothing moves for the users who were already being served correctly.
 */
const TOKENS: Record<string, string> = {
    day: 'dd',
    month: 'MM',
    year: 'yyyy',
};

/** What to fall back to when a locale formats dates in a shape we cannot express. */
const FALLBACK_INPUT_FORMAT = 'MM/dd/yyyy';

/** A numeric date -- `21.10.2019`, `2019-10-21`, `10/21/2019` -- in the viewer's locale. */
export function formatLocaleDate(date: Date, locale?: string): string {
    return new Intl.DateTimeFormat(locale).format(date);
}

/**
 * The ordering and separators `formatLocaleDate` uses, written as a Luxon pattern.
 *
 * `DatePicker` takes a format string rather than a locale, and uses the same one to
 * show a date and to read one the user typed, so the two have to agree.
 *
 * Numeric formats only. A locale that reaches for a month name, an era or a
 * non-Gregorian calendar produces parts this cannot express, and gets the fallback
 * rather than a pattern that would be wrong in a harder-to-notice way.
 */
export function localeDateInputFormat(locale?: string): string {
    const parts = new Intl.DateTimeFormat(locale).formatToParts(REFERENCE_DATE);
    const pattern = parts.map(({ type, value }) => (type === 'literal' ? value : TOKENS[type])).join('');

    return parts.every(({ type }) => type === 'literal' || TOKENS[type])
        ? pattern
        : FALLBACK_INPUT_FORMAT;
}
